// Utils
import { getImageWidthHeight, getShowBadge } from '../utils'

// Types
import type { Props } from '../product-card-gallery'
import { Maybe, ProductTypeProps } from '../index'
import { DigitalDataOrigin } from '../../utils/digital-data-types'

// Components
import ProductAvailability from '../../product-availability'
import { CompareButton } from '../../product-compare/product-compare'
import { Card } from '../product-card-gallery-composable'
import { FavoriteButton } from '../../product-add-to-favorites/favorites-button'
import { WindowBreakpoints } from '@dustin-web/microsite-config'

const ProductCardGalleryWithSideButtons = ({
  product,
  ticket,
  slim = false,
  withBorder = false,
  imagePriority = false,
  isExtraItem = false,
  inFilterList = false,
  sizeClasses = '',
  cardClasses = '',
  mainProductErpIdentifier,
  digitalDataOrigin,
  digitalIndex = 0,
  isProductAccessories = false,
  showFavoritesButton = true,
  primary = true,
  showActionRow = true,
  preventSetLastUpdatedProductErpIdentifier,
  breakpoints,
}: Props & { breakpoints: WindowBreakpoints }) => {
  const { defaultImageWidth, defaultImageHeight, mobileWidth, mobileHeight } =
    getImageWidthHeight(breakpoints)

  const imageWidth = cardClasses?.length ? mobileWidth : defaultImageWidth
  const imageHeight = cardClasses?.length ? mobileHeight : defaultImageHeight

  const showFavoritesButtonBreakpoint = showFavoritesButton && breakpoints.min.SM
  const categoryName = product.categoryName || product.category?.name

  return (
    <Card.Container
      digitalDataOrigin={digitalDataOrigin}
      digitalIndex={digitalIndex}
      product={product}
      sizeClasses={sizeClasses}
      isExtraItem={isExtraItem}
      inFilterList={inFilterList}>
      <Card.Wrapper
        cardClasses={cardClasses}
        showBadge={getShowBadge(product)}
        withBorder={withBorder}
        withSideButtons>
        <div className="d-flex c-product-card-with-sideButtons">
          <Card.Picture
            product={product}
            ticket={ticket}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            imagePriority={imagePriority}
            digitalDataIndex={digitalIndex}
            digitalDataOrigin={digitalDataOrigin}
            className="u-mb-0"
          />
          <Card.Content>
            <div>
              <Card.Title
                product={product}
                ticket={ticket}
                digitalDataIndex={digitalIndex}
                digitalDataOrigin={digitalDataOrigin}
              />

              <Card.SpecificationList slim={slim} list={product.promotedSpecifications} />
            </div>
            <Card.Center>
              <Card.Stars reviewCount={product.reviewCount} reviewScore={product.reviewScore} />
              <Card.Badge
                isAvailableForSale={product.availability?.isAvailableForSale}
                price={product.price}
                isNewProduct={product.isNewProduct}
              />

              <Card.Energy
                energyLabelImageId={product.energyLabelImageId}
                energyClass={product.energyClass}
                energyDocumentId={product.energyDocumentId}
                nameSlug={product.nameSlug}
                manufacturerName={product.manufacturerName}
                id={product.id}
                displayName={product.displayName}
                displaySpecifications={product.displaySpecifications}
                textAlignment={breakpoints.min.LG && !breakpoints.min.XL ? 'bottom' : 'right'}
                isMinSmallMobile={breakpoints.min.SM}
              />
              <Card.Price
                price={product.price}
                isAvailableForSale={product.availability?.isAvailableForSale}
              />
              <Card.ButtonRow>
                <Card.ToProductButton
                  productErpIdentifier={product.productErpIdentifier}
                  nameSlug={product.nameSlug}
                  product={product}
                  ticket={ticket}
                  mainProductErpIdentifier={mainProductErpIdentifier}
                  digitalDataOrigin={digitalDataOrigin}
                  digitalIndex={digitalIndex}
                  primary={primary}
                  showFavoritesButtonBreakpoint={showFavoritesButtonBreakpoint}
                  isMinSmallMobile={breakpoints.min.SM}
                  isProductAccessories={isProductAccessories}
                  preventSetLastUpdatedProductErpIdentifier={
                    preventSetLastUpdatedProductErpIdentifier
                  }
                />
                <Card.Favorites
                  show={showFavoritesButtonBreakpoint}
                  product={product}
                  digitalDataOrigin={digitalDataOrigin}
                />
              </Card.ButtonRow>
            </Card.Center>
          </Card.Content>
        </div>
        {showActionRow && (
          <div className="c-product-card__action-row u-py-8 u-relative">
            {product.availability && (
              <ProductAvailability
                availability={product.availability}
                isBid={product.price?.isBid}
                listForm={true}
              />
            )}
            {product.categoryName && (
              <CompareButton
                productId={product.id}
                displayName={product.displayName || ''}
                categoryName={categoryName || ''}
                listForm
              />
            )}
          </div>
        )}
        <SideButtons
          compare={{
            id: product.id,
            displayName: product.displayName || '',
            text: undefined,
            categoryName: categoryName,
            listForm: true,
          }}
          favorite={{
            showFavoritesButton,
            product,
            digitalDataOrigin,
          }}
        />
      </Card.Wrapper>
    </Card.Container>
  )
}

type SideButtonsProps = {
  compare: {
    id: string
    displayName: string
    categoryName: Maybe<string> | undefined
    noText?: boolean
    listForm: boolean
    text: string | undefined
  }
  favorite: {
    showFavoritesButton: boolean
    product: ProductTypeProps
    digitalDataOrigin: DigitalDataOrigin
    noText?: boolean
  }
}

const SideButtons = ({ compare, favorite }: SideButtonsProps) => (
  <div className="row xs-gutter c-product-card__action-buttons">
    {compare.categoryName && (
      <div className="col-6 d-flex align-items-center justify-content-center">
        <CompareButton
          productId={compare.id}
          displayName={compare.displayName}
          categoryName={compare.categoryName}
          listForm={compare.listForm}
          text={compare.text}
          noText={compare.noText}
        />
      </div>
    )}
    {favorite.showFavoritesButton && (
      <div className="col-6 d-flex align-items-center justify-content-center">
        <FavoriteButton
          product={favorite.product}
          digitalDataOrigin={favorite.digitalDataOrigin}
          noText={favorite.noText}
        />
      </div>
    )}
  </div>
)

export default ProductCardGalleryWithSideButtons
